import { queryOptions } from '@tanstack/react-query';

import type { paths } from '@/types/api';
import { ApiError, fetchClient, isApiErrorV2 } from '@/util/fetchClient';

type EmailConsentPathParams =
  paths['/v2/public/email-consent/{token}']['get']['parameters']['path'];

export type EmailConsent = NonNullable<
  paths['/v2/public/email-consent/{token}']['get']['responses'][200]['content']['application/json']['data']
>;

export type EmailConsentFailure = 'not-found' | 'expired' | 'rate-limited' | 'unknown';

/**
 * Classifies a failed consent call by status. The endpoint answers an unknown token with an
 * opaque 404 and an expired one with 410, and the screen words those differently, so the
 * status is the only signal there is. A 400 means the token failed the path's length check,
 * which is a link mangled in transit and reads to the reader exactly like an unknown one.
 */
export function classifyEmailConsentFailure(error: unknown): EmailConsentFailure {
  if (!(error instanceof ApiError) && !isApiErrorV2(error)) {
    return 'unknown';
  }

  if (error.status === 404 || error.status === 400) {
    return 'not-found';
  }

  if (error.status === 410) {
    return 'expired';
  }

  if (error.status === 429) {
    return 'rate-limited';
  }

  return 'unknown';
}

/**
 * Reads a consent request by its emailed token. Public and read-only, so it is safe on load.
 * The token is the whole grant, which is why nothing here sends credentials of its own.
 */
async function getEmailConsent({ token }: EmailConsentPathParams) {
  const { data } = await fetchClient.GET('/v2/public/email-consent/{token}', {
    params: { path: { token } },
  });

  if (!data?.data) {
    throw new Error('Email consent request not found');
  }

  return data.data;
}

export const getEmailConsentQueryOptions = ({ token }: EmailConsentPathParams) =>
  queryOptions<EmailConsent>({
    queryKey: ['emailConsent', token],
    queryFn: () => getEmailConsent({ token }),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

/**
 * Records acceptance. Idempotent on the server, so a repeat press or a retry after a dropped
 * response returns accepted again rather than an error.
 */
export async function acceptEmailConsent({ token }: EmailConsentPathParams) {
  const { data } = await fetchClient.POST('/v2/public/email-consent/{token}', {
    params: { path: { token } },
  });

  if (data?.data?.status !== 'accepted') {
    throw new Error('Email consent was not recorded');
  }

  return data.data;
}
